
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Project, RibaStage, Activity } from "@/types";
import { StageGanttBar } from "./StageGanttBar";
import { ActivityGanttBar } from "./ActivityGanttBar";
import { TimelineMarkers } from "./TimelineMarkers";
import { useIsMobile } from "@/hooks/use-mobile";

interface GanttChartViewProps {
  viewMode: "stage" | "activity";
  currentProject: Project;
  startDate: Date;
  endDate: Date;
  timelineScale: number;
}

export const GanttChartView: React.FC<GanttChartViewProps> = ({
  viewMode,
  currentProject,
  startDate,
  endDate,
  timelineScale
}) => {
  const isMobile = useIsMobile();
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-6 w-full overflow-x-auto">
          {/* Timeline header */}
          <div className="flex border-b pb-2 mb-4">
            {/* Label column - responsive width */}
            <div className="font-medium pr-4 w-1/4 min-w-16 max-w-48 truncate">
              {viewMode === "stage" ? "RIBA Stage" : "Activities"}
            </div>
            
            {/* Timeline column - flexible width */}
            <div className="flex-1 relative">
              {/* Month markers */}
              <TimelineMarkers startDate={startDate} endDate={endDate} />
            </div>
            
            {/* Status column - responsive width */}
            <div className="text-right font-medium w-1/5 min-w-16 max-w-24 truncate">
              Status
            </div>
          </div>
          
          {/* Content container with responsive width */}
          <div>
            {viewMode === "stage" ? (
              /* Gantt bars for stages */
              <div className="space-y-3">
                {currentProject.stages.map((stage) => (
                  <StageGanttBar 
                    key={stage.id}
                    stage={stage}
                    project={currentProject}
                    startDate={startDate}
                    endDate={endDate}
                    timelineScale={timelineScale}
                  />
                ))}
              </div>
            ) : (
              /* Activities grouped by stage */
              <div>
                {currentProject.stages.map((stage, stageIdx) => (
                  <div key={stage.id} className="mb-6">
                    {/* Stage header */}
                    <div className="font-medium text-md border-b border-gray-200 pb-2 mb-3 truncate">
                      {isMobile ? `Stage ${stage.id}` : `Stage ${stage.id}: ${stage.name}`}
                    </div>
                    
                    {/* Stage's activities */}
                    <div className={`pl-${isMobile ? '2' : '4'} border-l-2 border-primary/20`}>
                      {(stage.activities || []).length > 0 ? (
                        stage.activities.map((activity) => (
                          <ActivityGanttBar
                            key={activity.id}
                            activity={activity}
                            project={currentProject}
                            startDate={startDate}
                            endDate={endDate}
                            timelineScale={timelineScale}
                            stageIndex={stageIdx}
                          />
                        ))
                      ) : (
                        <div className="py-2 px-4 text-sm text-muted-foreground italic">
                          No activities
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
